<script lang="ts" setup>
import { computed } from 'vue';
import ProjectMetaData from '../ProjectMetaData.vue';
import type { ProjectMetaDataMode } from '../types';
import { useProjectMetaDataMutation } from './mutation';
import Skeleton from './ProjectMetaDataSkeleton.vue';
import { useProjectMetaDataQuery } from './query';

type NullPartial<T> = {
  [P in keyof T]?: Exclude<T[P], ''> | null;
};

const props = defineProps<{
  projectId: string;
  mode?: ProjectMetaDataMode;
}>();

const { data } = useProjectMetaDataQuery(props.projectId);
const mutation = useProjectMetaDataMutation({ projectId: props.projectId });

const isFirstLoad = computed(() => data.query.isLoading.value);

const logicProps = computed(() => {
  const or = <T,>(val: T | null | undefined, defaultValue: T) =>
    val ?? defaultValue;
  const q = data.query.data.value;

  return {
    isSaving: mutation.isPending.value,
    data: {
      name: or(q?.name, ''),
      number: or(q?.fortnoxId, ''),
      status: or(q?.status, ''),
      type: or(q?.type, ''),
      contractorType: or(q?.contractorType, ''),
      size: or(q?.size, 0),
      compensationForm: or(q?.compensationForm, ''),
      contractSum: or(q?.contractSum, 0),
      arvode: q?.arvode ?? null,
      startDate: or(q?.startDate, ''),
      endDate: or(q?.endDate, ''),
      samprovDate: or(q?.samprovDate, ''),
      douinDate: or(q?.douinDate, ''),
      handoverDate: or(q?.handoverDate, ''),
      fdobDate: or(q?.fdobDate, ''),
      earnings: q?.earnings ?? null,
      earningsPercent: q?.earningsPercent ?? null,
      ataSum: or(q?.ataSum, 0),
      undertakingSum: q?.undertakingSum ?? null,
    },
    mode: props.mode ?? 'read-only',
  };
});

type OnSaveEvent = NonNullable<
  InstanceType<typeof ProjectMetaData>['$props']['onSave']
>;

const onSave: OnSaveEvent = (data) => {
  const requiredFields = {
    name: data.name,
    number: data.number,
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { contractSum: _contractSum, ...rest } = data;
  const body = Object.fromEntries(
    Object.entries(rest).map(([k, v]) => [k, v === '' ? null : v]),
  ) as NullPartial<typeof rest>;
  mutation.mutate({
    ...body,
    ...requiredFields,
  });
};
</script>

<template>
  <Skeleton v-if="isFirstLoad" />
  <ProjectMetaData v-else v-bind="logicProps" @save="onSave" />
</template>
